/**
 * O formulário "Cadastrar no SISAC" (#1024): o que vem preenchido do contato e
 * o corpo que vai à API. Sem React, para o teste não precisar de tela.
 */

import { ErroDaApi, type CadastrarPacienteDoCliente } from '@cliqcrm/contracts';
import { soDigitos, telefoneParaOSisac } from '@/entities/saude/paciente';

export interface RascunhoDoCadastro {
  readonly nome: string;
  readonly cpf: string;
  readonly sexo: '' | 'F' | 'M';
  /** `AAAA-MM-DD`, como o `<input type="date">` dá. */
  readonly nascimento: string;
  readonly celular: string;
  readonly email: string;
}

/** O celular da conversa sem o 55: o SISAC guarda DDD e número. */
export function celularDoContato(telefone: string | null): string {
  return telefoneParaOSisac(telefone);
}

export function rascunhoInicial(contato: {
  readonly nome: string | null;
  readonly telefone: string | null;
  readonly email: string | null;
  readonly nascimento: string | null;
  readonly cpf: string;
}): RascunhoDoCadastro {
  return {
    nome: contato.nome ?? '',
    cpf: contato.cpf,
    sexo: '',
    nascimento: contato.nascimento ?? '',
    celular: celularDoContato(contato.telefone),
    email: contato.email ?? '',
  };
}

export type ErrosDoCadastro = Partial<Record<keyof RascunhoDoCadastro, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function errosDoCadastro(r: RascunhoDoCadastro, hoje: string): ErrosDoCadastro {
  const erros: ErrosDoCadastro = {};
  if (r.nome.trim().length < 3) erros.nome = 'Escreva o nome completo.';
  if (soDigitos(r.cpf).length !== 11) erros.cpf = 'O CPF tem 11 dígitos.';
  if (r.sexo === '') erros.sexo = 'Escolha o sexo, como o SISAC pede.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.nascimento)) erros.nascimento = 'Informe o nascimento.';
  else if (r.nascimento > hoje) erros.nascimento = 'O nascimento não pode ser no futuro.';
  const celular = soDigitos(r.celular);
  if (celular.length !== 10 && celular.length !== 11) erros.celular = 'Celular com DDD.';
  if (r.email.trim() !== '' && !EMAIL.test(r.email.trim())) erros.email = 'E-mail inválido.';
  return erros;
}

/**
 * O SISAC já tem paciente com este CPF: o servidor pede confirmação (409 com
 * `cpf: ja_cadastrado`), como o próprio SISAC pergunta "Encontrado outro
 * paciente com o mesmo CPF. Continuar?".
 */
export function cpfJaCadastrado(erro: unknown): boolean {
  return (
    erro instanceof ErroDaApi &&
    erro.status === 409 &&
    (erro.problema.errors ?? []).some((e) => e.campo === 'cpf' && e.problema === 'ja_cadastrado')
  );
}

/**
 * O corpo da rota; chame só sem erros. `confirmarCpfRepetido` é a resposta
 * "Continuar" à pergunta do CPF repetido: cadastra outro paciente com o mesmo CPF.
 */
export function corpoDoCadastro(
  r: RascunhoDoCadastro,
  confirmarCpfRepetido = false,
): CadastrarPacienteDoCliente {
  return {
    ...(confirmarCpfRepetido ? { confirmar_cpf_repetido: true } : {}),
    nome: r.nome.trim(),
    cpf: soDigitos(r.cpf),
    sexo: r.sexo === 'M' ? 'M' : 'F',
    data_nasc: r.nascimento,
    celular: soDigitos(r.celular),
    email: r.email.trim() === '' ? null : r.email.trim(),
  };
}
